import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/20/solid";
import { UserIcon } from "@heroicons/react/24/outline";
import { useAuth, useSignIn } from "@clerk/clerk-react";
import clsx from "clsx";
import DividerWithText from "../../components/dividerWithText";
import { routeEndpoints } from "../../routes/routeEndpoints";
import { useGoBack } from "../../hooks/useGoBack";
import { clerkErrorMessage } from "../../utils/clerkError";

type LoginFormValues = {
  email: string;
  password: string;
};

export default function LoginPage() {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { userId, isLoaded: isAuthLoaded } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();
  const [showPassword, setShowPassword] = useState(false);
  const [preventAutofill, setPreventAutofill] = useState(true);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>();

  useEffect(() => {
    if (isAuthLoaded && userId) {
      navigate(routeEndpoints.orders, { replace: true });
    }
  }, [isAuthLoaded, userId, navigate]);

  const onSubmit = async (data: LoginFormValues) => {
    if (!isLoaded) return;

    try {
      const attempt = await signIn.create({
        identifier: data.email,
        password: data.password,
      });

      if (attempt.status === "complete") {
        await setActive({ session: attempt.createdSessionId });
        navigate(routeEndpoints.orders, { replace: true });
      }
    } catch (error) {
      setError("root", { message: clerkErrorMessage(error) });
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col font-sans">
      <div className="w-full max-w-xl md:ml-28 mt-9 px-4 md:px-0">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium cursor-pointer"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-xl shadow-sm border border-slate-100 md:px-10 md:py-9 px-6 py-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center justify-center size-14 rounded-full bg-blue-50">
                <UserIcon className="size-6 text-blue-600" />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="md:text-2xl md:font-semibold text-xl font-medium text-gray-950">
                  Login
                </h1>
                <p className="text-sm md:font-medium font-normal text-gray-400">
                  Access your orders and account details.
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-8"
            >
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium text-gray-950"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    readOnly={preventAutofill}
                    onFocus={() => setPreventAutofill(false)}
                    placeholder="Enter your email"
                    className={clsx(
                      "w-full rounded-xl border px-4 py-3.5 text-sm outline-none focus:ring-2 placeholder:text-gray-400",
                      errors.email
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-200 focus:ring-blue-500",
                    )}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Must be a valid email address",
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="password"
                    className="text-sm font-medium text-gray-950"
                  >
                    Password
                  </label>
                  <div
                    className={clsx(
                      "flex items-center gap-2 rounded-xl border px-4 py-3.5 focus-within:ring-2",
                      errors.password
                        ? "border-red-500 focus-within:ring-red-500"
                        : "border-gray-200 focus-within:ring-blue-500",
                    )}
                  >
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      readOnly={preventAutofill}
                      onFocus={() => setPreventAutofill(false)}
                      placeholder="Create a strong password"
                      className="flex-1 min-w-0 text-sm outline-none placeholder:text-gray-400"
                      {...register("password", {
                        required: "Password is required",
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="text-gray-400 hover:text-gray-500 cursor-pointer"
                    >
                      {showPassword ? (
                        <EyeIcon className="size-5" />
                      ) : (
                        <EyeSlashIcon className="size-5" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    {errors.password ? (
                      <p className="text-sm text-red-500">
                        {errors.password.message}
                      </p>
                    ) : (
                      <div />
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(routeEndpoints.forgotPassword)}
                      className="text-sm font-medium text-gray-400 hover:text-gray-500 cursor-pointer"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col">
                {errors.root && (
                  <p className="mb-4 text-sm text-center text-red-500">
                    {errors.root.message}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!isLoaded || isSubmitting}
                  className="w-full h-13 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Logging in…" : "Log in"}
                </button>

                <DividerWithText text="Don't have an account?" />

                <button
                  type="button"
                  onClick={() =>
                    navigate(routeEndpoints.signup, { replace: true })
                  }
                  className="w-full h-13 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors"
                >
                  Create an account
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
